import type { FC } from "react";

import type {
  PlanSnapshotDependency,
  PlanSnapshotNodeWithDependencyCounts,
} from "../../types";

import {
  AccentCard,
  CARD_DESC_CLASS,
  CARD_STATUS_CLASS,
  CARD_TITLE_CLASS,
  EmptyState,
  StatusDot,
  getStatusAccent,
  getStatusDot,
} from "../common";
import { SectionHeader } from "../common/SectionHeader";

export interface PlanSnapshotDrawerDependencyListItem {
  dep: PlanSnapshotDependency;
  resolved: PlanSnapshotNodeWithDependencyCounts | null;
}

export interface PlanSnapshotDrawerDependencyListProps {
  items: PlanSnapshotDrawerDependencyListItem[];
  onSelectNode: (nodeId: string) => void;
}

export const PlanSnapshotDrawerDependencyList: FC<
  PlanSnapshotDrawerDependencyListProps
> = ({ items, onSelectNode }) => (
  <section>
    <SectionHeader
      variant="default"
      title="Dependencies"
      badge={String(items.length)}
      className="mb-0 text-xs"
    />
    {items.length === 0 ? (
      <EmptyState
        size="sm"
        title="No dependencies"
        description="This node does not depend on any other tracked tasks."
      />
    ) : (
      <div className="space-y-1">
        {items.map(({ dep, resolved }) => {
          const status = (resolved?.status ?? "").toLowerCase();

          return (
            <AccentCard
              key={`${dep.depends_on_key}:${dep.depends_on_node_id ?? "key"}`}
              accent={resolved ? getStatusAccent(status) : "border-l-amber-400"}
              onClick={
                resolved ? () => onSelectNode(resolved.node_id) : undefined
              }
              disabled={!resolved}
              title={
                resolved
                  ? "Open dependency details"
                  : "Dependency not found in this snapshot"
              }
            >
              <div className="flex items-center gap-1.5">
                <StatusDot
                  color={resolved ? getStatusDot(status) : "bg-amber-400"}
                />
                <span className={CARD_TITLE_CLASS}>{dep.depends_on_key}</span>
                {!resolved && (
                  <span className="flex-shrink-0 text-xs font-medium text-amber-500 dark:text-amber-400">
                    unresolved
                  </span>
                )}
                {resolved?.status && (
                  <span className={CARD_STATUS_CLASS}>{resolved.status}</span>
                )}
              </div>
              {resolved &&
                (resolved.title ??
                  resolved.humanized_description ??
                  resolved.description) && (
                  <p className={CARD_DESC_CLASS}>
                    {resolved.title ??
                      resolved.humanized_description ??
                      resolved.description}
                  </p>
                )}
            </AccentCard>
          );
        })}
      </div>
    )}
  </section>
);
